import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { interval, of, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { PublishingStatusStateService } from '../../../services/publishing-status/publishing-status-state.service';
import {
  GetPublicationsPageParams,
  PublicationsPageResult,
  PublishingRepository,
} from '../../../infrastructure/repositories/publishing-status/publishing.repository';
import {
  PublishingStatus,
  PublishingDateRangeKey,
} from '../../../domain/publishing-status';
import { PUBLISHING_LIST_POLL_INTERVAL_MS } from '../publishing-status.constants';

type RefreshRequest = { silent: boolean };

type RefreshOutcome =
  | { kind: 'success'; silent: boolean; page: PublicationsPageResult }
  | { kind: 'error'; silent: boolean; err: unknown };

@Component({
  selector: 'evrideo-publishing-status-container',
  templateUrl: './publishing-status-container.component.html',
  styleUrls: ['./publishing-status-container.component.scss'],
})
export class PublishingStatusContainerComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly refresh$ = new Subject<RefreshRequest>();

  constructor(
    public state: PublishingStatusStateService,
    private repository: PublishingRepository,
    private router: Router,
    private toastr: ToastrService,
  ) {}

  onAddNew(): void {
    this.router.navigateByUrl('/clipping-studio/mam-explorer');
  }

  ngOnInit(): void {
    this.refresh$
      .pipe(
        switchMap(({ silent }) => {
          if (!silent) {
            this.state.setLoading(true);
            this.state.setError(null);
          }

          return this.repository.getPublicationsPage(this.getPageParams()).pipe(
            map(
              (page): RefreshOutcome => ({
                kind: 'success',
                silent,
                page,
              }),
            ),
            catchError((err) =>
              of<RefreshOutcome>({ kind: 'error', silent, err }),
            ),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((outcome) => this.handleRefreshOutcome(outcome));

    this.loadPage();

    interval(PUBLISHING_LIST_POLL_INTERVAL_MS)
      .pipe(
        takeUntil(this.destroy$),
        filter(() => !this.state.isLoading() && !this.state.isBatchBusy()),
      )
      .subscribe(() => this.refreshPage({ silent: true }));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.state.reset();
  }

  loadPage(): void {
    this.refreshPage({ silent: false });
  }

  onSearchChange(query: string): void {
    this.state.setSearchQuery(query);
    this.state.setOffset(0);
    this.loadPage();
  }

  onStatusChange(status: PublishingStatus | 'All'): void {
    this.state.setStatusFilter(status);
    this.state.setOffset(0);
    this.loadPage();
  }

  onDateRangeChange(key: PublishingDateRangeKey): void {
    this.state.setDateRangeKey(key);
    this.state.setOffset(0);
    this.loadPage();
  }

  onPageChange(pageIndex: number): void {
    this.state.setOffset(pageIndex * this.state.pageSize());
    this.loadPage();
  }

  onPageSizeChange(pageSize: number): void {
    this.state.setPageSize(pageSize);
    this.state.setOffset(0);
    this.loadPage();
  }

  onSelectionChange(change: { id: string; selected: boolean }): void {
    this.state.toggleSelection(change.id, change.selected);
  }

  onPublicationCancelled(): void {
    this.refreshPage({ silent: true });
  }

  async onImportToMam(): Promise<void> {
    if (!this.state.canExportToMam()) {
      return;
    }

    const publicationUids = this.state.getSelectedPublicationUids();
    if (publicationUids.length === 0) {
      return;
    }

    this.state.setExportingToMam(true);
    try {
      await this.repository.exportToMam(publicationUids);
      this.toastr.success('Imported to MAM successfully!', 'Success', {});
      this.state.clearSelection();
      this.refreshPage({ silent: true });
    } catch {
      this.toastr.error('Error while importing to MAM!', 'Error', {});
    } finally {
      this.state.setExportingToMam(false);
    }
  }

  async onDeleteSelected(): Promise<void> {
    if (!this.state.canDelete()) {
      return;
    }

    const publicationUids = this.state.getSelectedPublicationUids();
    if (publicationUids.length === 0) {
      return;
    }

    const confirmed = window.confirm(
      `Delete ${publicationUids.length} selected publication(s)? This action may be irreversible.`,
    );
    if (!confirmed) {
      return;
    }

    this.state.setDeleting(true);
    try {
      await this.repository.deletePublications(publicationUids);
      this.toastr.success('Deleted successfully!', 'Success', {});
      this.state.clearSelection();
      this.refreshPage({ silent: true });
    } catch {
      this.toastr.error('Error while deleting!', 'Error', {});
    } finally {
      this.state.setDeleting(false);
    }
  }

  private refreshPage(request: RefreshRequest): void {
    this.refresh$.next(request);
  }

  private getPageParams(): GetPublicationsPageParams {
    return {
      limit: this.state.pageSize(),
      offset: this.state.offset(),
      query: this.state.searchQuery(),
      statusFilter: this.state.statusFilter(),
      dateRangeKey: this.state.dateRangeKey(),
    };
  }

  private handleRefreshOutcome(outcome: RefreshOutcome): void {
    if (outcome.kind === 'success') {
      this.state.setPageResult(outcome.page);
      if (!outcome.silent) {
        this.state.setLoading(false);
      }
      return;
    }

    if (outcome.silent) {
      console.warn('Publications poll failed', outcome.err);
      return;
    }

    const message =
      outcome.err instanceof Error
        ? outcome.err.message
        : 'Unable to load distribution events';
    this.state.setError(message);
    this.state.setLoading(false);
  }
}
